import React from 'react';
import { useLocation } from 'react-router-dom';
import { SkeletonHeader } from '../Header/skeleton/SkeletonHeader';
import { SkeletonNavigation } from '../Header/skeleton/SkeletonNavigation';
import { SkeletonCard } from '../Header/skeleton/SkeletonCard';
// Imported from the component files (not the feature barrels) so the route
// skeleton doesn't pull each feature's whole module graph into the initial
// bundle — these render *while* the lazy route is still loading.
import { AgentSettingsSkeleton } from '../../features/agent-settings/pages/AgentSettingsSkeleton';
import { AccessVerificationSkeleton } from '../../features/access-verification/components/AccessVerificationSkeleton';
import { HomePageSkeleton } from '../../features/home/components/HomePageSkeleton/HomePageSkeleton';
import { AgentOverviewSkeleton } from '../../features/home/pages/AgentOverviewPage/AgentOverviewSkeleton';
import { AppShellSkeleton } from '../../features/navigation/components/AppShellSkeleton/AppShellSkeleton';
import { AuthPageSkeleton } from '../../features/auth/skeletons/AuthPageSkeleton';
import { ResetPasswordSkeleton } from '../../features/auth/skeletons/ResetPasswordSkeleton';
import { OrganizationRouteSkeleton } from '../../features/organizations/skeletons/OrganizationRouteSkeleton';
import { AccountsPageSkeleton } from '../../features/account/components/AccountSkeleton';
import { AccountSettingsSkeleton } from '../../features/account/components/AccountSettings/AccountSettingsSkeleton';
import { ProfileSkeleton } from '../../features/account/components/Profile/ProfileSkeleton';
import { DataViewsPageSkeleton } from '../../features/data-views/components/DataViewsLoadingSkeleton/DataViewsPageSkeleton';
import { DashboardRouteSkeleton } from '../../features/dashboards/components/DashboardSkeleton/DashboardRouteSkeleton';
import { dashboardSkeletonKind } from '../../features/dashboards/components/DashboardSkeleton/dashboardSkeletonKind';
import { AgentCallsSkeleton } from '../../features/inbox-agent-calls/components/AgentCallsSkeleton/AgentCallsSkeleton';
import { isAgentCallsPath } from '../../features/inbox-agent-calls/components/AgentCallsSkeleton/isAgentCallsPath';
import { InboxChatsSkeleton } from '../../features/inbox/components/InboxChatsSkeleton/InboxChatsSkeleton';
import { isInboxChatsPath } from '../../features/inbox/components/InboxChatsSkeleton/isInboxChatsPath';
import { KnowledgeSkeleton } from '../../features/knowledge/components/KnowledgeSkeleton/KnowledgeSkeleton';
import { isKnowledgePath } from '../../features/knowledge/components/KnowledgeSkeleton/isKnowledgePath';

export function RouteSkeleton(): React.ReactElement {
  const location = useLocation();

  // Signed-out screens — their own skeletons (no app chrome: there's no
  // session, so MainLayout never mounts). Reset password is a centered card,
  // not the brand layout, so it gets its own shape.
  if (location.pathname === '/login' || location.pathname === '/recoveraccount') {
    return <AuthPageSkeleton />;
  }

  if (location.pathname === '/resetpassword') {
    return <ResetPasswordSkeleton />;
  }

  // Dashboards (/accounts/:id/dashboards/new, new/editor, :id, :id/editor) —
  // the section's own skeleton inside the app chrome, the same one its route
  // uses as Suspense fallback (SL-1901). Before the `/home` branch: that one is
  // a substring test, and a dashboard id is free text in the URL.
  if (dashboardSkeletonKind(location.pathname)) {
    return (
      <AppShellSkeleton>
        <DashboardRouteSkeleton />
      </AppShellSkeleton>
    );
  }

  // Home — the app chrome around the feature's own skeleton (the same one its
  // route uses as Suspense fallback), so a refresh paints sidebar + page from
  // the first frame and the page-loading phase looks the same.
  if (location.pathname.includes('/home')) {
    return (
      <AppShellSkeleton>
        <HomePageSkeleton />
      </AppShellSkeleton>
    );
  }

  // Organization (users / teams / roles) — same pattern as Home.
  if (/\/accounts\/[^/]+\/organization(\/|$)/.test(location.pathname)) {
    return (
      <AppShellSkeleton>
        <OrganizationRouteSkeleton />
      </AppShellSkeleton>
    );
  }

  // 🧩 MODULES - Usar SkeletonHeader
  if (location.pathname.includes('/modules')) {
    return <SkeletonHeader />;
  }

  // 🏢 ACCOUNT ROUTES - Usar skeletons específicos

  // Account management page (debe ir antes que accounts genérico)
  if (
    location.pathname.includes('/accounts/') &&
    location.pathname.includes('/accounts/management')
  ) {
    return <SkeletonCard />;
  }

  // Account new page (debe ir antes que accounts genérico)
  if (location.pathname.includes('/accounts/') && location.pathname.includes('/accounts/new')) {
    return <SkeletonCard />;
  }

  // Data View (/accounts/:id/data-views/:tableId, and the agent-nested
  // data-views routes) — before the generic assistants branch (SL-1900). Not
  // the QA agent's data-views, which only redirects to the table. Two tests,
  // not a lookbehind: Babel can't transpile one and Safari < 16.4 rejects it.
  if (
    /\/data-views(\/[^/]+)?\/?$/.test(location.pathname) &&
    !/\/qa\/data-views(\/[^/]+)?\/?$/.test(location.pathname)
  ) {
    return (
      <AppShellSkeleton>
        <DataViewsPageSkeleton />
      </AppShellSkeleton>
    );
  }

  // Accounts Manager (/accounts/:id/accounts) — its own skeleton inside the
  // app chrome, the same one the route uses as Suspense fallback.
  if (/\/accounts\/[^/]+\/accounts\/?$/.test(location.pathname)) {
    return (
      <AppShellSkeleton>
        <AccountsPageSkeleton />
      </AppShellSkeleton>
    );
  }

  // Account root page with ID (debe ir después de las rutas específicas)
  if (location.pathname.match(/\/accounts\/[^/]+\/?$/)) {
    return <SkeletonHeader />;
  }

  // 🤖 ASSISTANTS - Rutas específicas con SkeletonNavigation
  if (location.pathname.includes('/accounts/') && location.pathname.includes('/assistants')) {
    // Agent Overview — its own skeleton (see the Home branch above).
    if (/\/assistants\/[^/]+\/overview\/?$/.test(location.pathname)) {
      return (
        <AppShellSkeleton>
          <AgentOverviewSkeleton />
        </AppShellSkeleton>
      );
    }

    // Agent Settings hub (tabs Whitelist + Billing) — layout propio.
    if (location.pathname.includes('/settings')) {
      return <AgentSettingsSkeleton />;
    }

    // Access & Verification standalone — layout propio.
    if (location.pathname.includes('/access-verification')) {
      return <AccessVerificationSkeleton />;
    }

    // Inbox → Agent Calls — its own skeleton inside the app chrome, the same
    // one its route uses as Suspense fallback (SL-1908). Before the generic
    // `/inbox` test below.
    if (isAgentCallsPath(location.pathname)) {
      return (
        <AppShellSkeleton>
          <AgentCallsSkeleton standalone />
        </AppShellSkeleton>
      );
    }

    // Inbox → Chats — its own skeleton too (SL-1908, part 4). Anchored, and
    // before the generic `/inbox` test below, which keeps the other inbox paths
    // (`/inbox/calls`, the legacy `/inbox` redirect) on SkeletonNavigation.
    if (isInboxChatsPath(location.pathname)) {
      return (
        <AppShellSkeleton>
          <InboxChatsSkeleton standalone />
        </AppShellSkeleton>
      );
    }

    // Knowledge base — its own skeleton inside the app chrome, the same one its
    // route uses as Suspense fallback (SL-1909, part 2). Anchored, and before
    // the generic `/myassistant/knowledge` test below, which keeps anything
    // nested under it on SkeletonNavigation.
    if (isKnowledgePath(location.pathname)) {
      return (
        <AppShellSkeleton>
          <KnowledgeSkeleton standalone />
        </AppShellSkeleton>
      );
    }

    // Rutas específicas que usan SkeletonNavigation
    if (
      location.pathname.includes('/inbox') ||
      location.pathname.includes('/myassistant/flows') ||
      location.pathname.includes('/myassistant/process') ||
      location.pathname.includes('/myassistant/knowledge') ||
      location.pathname.includes('/myassistant/channels') ||
      location.pathname.includes('/assistants/new/channels') ||
      location.pathname.includes('/myassistant/queues') ||
      location.pathname.includes('/myassistant/appearance') ||
      location.pathname.includes('/myassistant/personality') ||
      location.pathname.includes('/myassistant/metrics') ||
      location.pathname.includes('/myassistant/team-overview') ||
      location.pathname.includes('/metrics')
    ) {
      return <SkeletonNavigation />;
    }

    // Todas las demás rutas de assistants usan SkeletonCard
    return <SkeletonCard />;
  }

  // Billing (/accounts/:id/settings/account) — its own skeleton inside the app
  // chrome, the same one its route uses as Suspense fallback (SL-1898).
  if (/\/accounts\/[^/]+\/settings\/account\/?$/.test(location.pathname)) {
    return (
      <AppShellSkeleton>
        <AccountSettingsSkeleton />
      </AppShellSkeleton>
    );
  }

  // Profile (/accounts/:id/profile) — same pattern as Billing (SL-1899).
  if (/\/accounts\/[^/]+\/profile\/?$/.test(location.pathname)) {
    return (
      <AppShellSkeleton>
        <ProfileSkeleton />
      </AppShellSkeleton>
    );
  }

  // ⚙️ SETTINGS - Usar SettingsSkeleton
  if (location.pathname.includes('/accounts/') && location.pathname.includes('/settings')) {
    return <SkeletonNavigation />;
  }

  // 📊 METRICS - Usar SkeletonNavigation
  if (location.pathname.includes('/accounts/') && location.pathname.includes('/metrics')) {
    return <SkeletonHeader />;
  }

  // 🔧 DEFAULT - Usar SkeletonNavigation para todas las demás rutas
  return <SkeletonNavigation />;
}
